import { useEffect, useState } from "react";
// material
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import axios from "@utils/axios";
import { MembershipChangeType, MembershipGroupType, MembershipSnapshot } from "types/admin";

const ACTION_LABEL: Record<MembershipChangeType["action"], { text: string; color: "success" | "info" | "error" }> = {
  created: { text: "생성", color: "success" },
  updated: { text: "수정", color: "info" },
  deleted: { text: "삭제", color: "error" },
};

/** 무엇이 바뀌었는지만 골라 보여준다. 전체 스냅샷을 나열하면 정작 변화가 안 보인다. */
function diff(before: MembershipSnapshot | null, after: MembershipSnapshot | null): string[] {
  if (!before || !after) return [];
  const labels: Record<keyof MembershipSnapshot, string> = {
    name: "이름",
    level: "등급",
    start_date: "시작일",
    end_date: "종료일",
  };
  return (Object.keys(labels) as (keyof MembershipSnapshot)[])
    .filter((key) => before[key] !== after[key])
    .map((key) => `${labels[key]}: ${before[key] ?? "없음"} → ${after[key] ?? "없음"}`);
}

type Props = {
  group: MembershipGroupType | null;
  onClose: () => void;
};

export default function MembershipHistoryDialog({ group, onClose }: Props) {
  const [rows, setRows] = useState<MembershipChangeType[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!group) {
      setRows(null);
      setFailed(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const response = await axios.get(`memberships/${group.id}/changes`);
        if (alive) setRows(response.data);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    // 다이얼로그가 닫힌 뒤 응답이 오면 사라진 화면에 setState 한다. 막는다.
    return () => {
      alive = false;
    };
  }, [group]);

  return (
    <Dialog open={Boolean(group)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        변경 이력
        <Typography variant="body2" color="text.secondary">
          {group?.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {failed && <Alert severity="error">이력을 불러오지 못했습니다.</Alert>}
        {rows !== null && rows.length === 0 && (
          // 화면이 생기기 전에 만들어진 그룹은 이력이 없다. 그게 정상이다.
          <Alert severity="info">
            기록된 변경이 없습니다. 이력은 이 화면이 생긴 뒤의 변경부터 남습니다.
          </Alert>
        )}
        <Stack spacing={2}>
          {(rows ?? []).map((row) => {
            const label = ACTION_LABEL[row.action];
            const changes = diff(row.before, row.after);
            return (
              <Stack key={row.id} spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={label.text} color={label.color} />
                  <Typography variant="caption" color="text.secondary">
                    {row.createdAt?.slice(0, 19).replace("T", " ")}
                  </Typography>
                </Stack>
                {row.action === "updated" && changes.length > 0 && (
                  <Stack sx={{ pl: 1 }}>
                    {changes.map((line) => (
                      <Typography key={line} variant="body2">
                        {line}
                      </Typography>
                    ))}
                  </Stack>
                )}
                {row.action === "created" && row.after && (
                  <Typography variant="body2" sx={{ pl: 1 }}>
                    {row.after.name} · {row.after.level} · {row.after.start_date} ~{" "}
                    {row.after.end_date}
                  </Typography>
                )}
                {row.action === "deleted" && row.before && (
                  <Typography variant="body2" sx={{ pl: 1 }}>
                    {row.before.name} · {row.before.level}
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}
